import { buildRegistrationProfilePayload } from '../utils/profileFieldMapping';
import {
  API_KEY,
  CLIENT_APP,
  IDENTITY_SERVICE_BASE_URL,
  PROFILE_SERVICE_BASE_URL,
} from './clientConfig';

const API_BASE = IDENTITY_SERVICE_BASE_URL;
const PROFILE_API_BASE = PROFILE_SERVICE_BASE_URL;

const STORAGE_KEYS = {
  access: 'olitask_access_token',
  refresh: 'olitask_refresh_token',
  user: 'olitask_user',
  expiry: 'olitask_token_expiry',
  clientId: 'olitask_client_id',
};

const SESSION_STORAGE_KEYS = [
  STORAGE_KEYS.access,
  STORAGE_KEYS.refresh,
  STORAGE_KEYS.user,
  STORAGE_KEYS.expiry,
];

SESSION_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

function setSessionItem(key: string, value: string) {
  sessionStorage.setItem(key, value);
  localStorage.removeItem(key);
}

function removeSessionItem(key: string) {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
}

export function saveUser(userProfile: any): void {
  if (!userProfile) {
    removeSessionItem(STORAGE_KEYS.user);
    return;
  }

  setSessionItem(STORAGE_KEYS.user, JSON.stringify(userProfile));
}

// =========== Helper Functions ===========
export async function getClientId(): Promise<string | null> {
  return getOrCreateClientId();
}

async function getOrCreateClientId(): Promise<string | null> {
  let clientId = localStorage.getItem(STORAGE_KEYS.clientId);
  if (clientId && clientId !== 'undefined' && clientId !== 'null') {
    return clientId;
  }

  try {
    console.log('📋 Registering new client...');
    const response = await fetch(`https://dev.oliveeb.com/api/v1/clients/register/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'O-API-KEY': API_KEY,
        'O-CLIENT-APP': CLIENT_APP,
      }
    });

    if (response.ok) {
      const data = await response.json();
      clientId = String(data.id || '');
      localStorage.setItem(STORAGE_KEYS.clientId, clientId);
      console.log('✅ New client registered:', clientId);
      return clientId;
    } else {
      console.error('❌ Failed to register client:', response.status);
    }
  } catch (error) {
    console.error('❌ Failed to get clientId:', error);
  }
  return null;
}

function saveSession(authResponse: any, userProfile: any) {
  setSessionItem(STORAGE_KEYS.access, authResponse.accessToken);
  if (authResponse.refreshToken) {
    setSessionItem(STORAGE_KEYS.refresh, authResponse.refreshToken);
  }

  if (!localStorage.getItem(STORAGE_KEYS.clientId)) {
    const fallbackClientId = authResponse.clientId || authResponse.client_id || '';
    if (fallbackClientId) {
      localStorage.setItem(STORAGE_KEYS.clientId, fallbackClientId);
    }
  }
  if (authResponse.accessTokenExpiresAt) {
    setSessionItem(STORAGE_KEYS.expiry, authResponse.accessTokenExpiresAt);
  }
  if (userProfile) {
    saveUser({
      ...userProfile,
      lastLoginAt: userProfile.lastLoginAt || userProfile.lastLogin || new Date().toISOString(),
    });
  }
  console.log('✅ Session saved - Token exists:', !!authResponse.accessToken);
}

function clearSession() {
  SESSION_STORAGE_KEYS.forEach(removeSessionItem);
  localStorage.removeItem(STORAGE_KEYS.clientId);
  sessionStorage.removeItem(STORAGE_KEYS.clientId);
  console.log('✅ Session cleared');
}

function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload.padEnd(Math.ceil(payload.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export function normalizeUserProfile(profile: any, fallback: any = {}) {
  if (!profile && !fallback) return null;

  const userIdentityVerification =
    profile?.userIdentityVerification ||
    profile?.identityVerification ||
    fallback?.userIdentityVerification;
  const userWorkAuthorization =
    profile?.userWorkAuthorization ||
    profile?.userWorkAthorization ||
    fallback?.userWorkAuthorization;
  const identityRecordVerified =
    userIdentityVerification?.isVerified === true ||
    userIdentityVerification?.verified === true ||
    String(userIdentityVerification?.verificationDecision || '').toUpperCase() === 'APPROVED';
  const contactDetails = profile?.contactDetails || {};
  const permanentAddress = profile?.permanentAddress || profile?.address || {};
  const residentialAddress = profile?.residentialAddress || fallback?.residentialAddress;
  const workAddress = profile?.workAddress || fallback?.workAddress;
  const schoolAddress = profile?.schoolAddress || fallback?.schoolAddress;
  const billingAddress = profile?.billingAddress || fallback?.billingAddress;
  const profilePhoto =
    profile?.profilePhoto ||
    profile?.picture ||
    profile?.profilePictureUrl ||
    profile?.imageUrl ||
    fallback?.picture ||
    '';

  return {
    ...(fallback || {}),
    ...(profile || {}),
    id: profile?.id || fallback?.id,
    firstName: profile?.firstName || fallback?.firstName || '',
    middleName: profile?.middleName || fallback?.middleName || '',
    lastName: profile?.lastName || fallback?.lastName || '',
    email:
      profile?.email ||
      contactDetails.personalEmail ||
      contactDetails.email ||
      fallback?.email ||
      '',
    username: profile?.username || fallback?.username || '',
    phone:
      profile?.phone ||
      contactDetails.mobilePhone ||
      contactDetails.phone ||
      fallback?.phone ||
      '',
    picture: profilePhoto,
    profilePhoto,
    securityPhoto: profile?.securityPhoto || fallback?.securityPhoto || '',
    securityPhotoAt: profile?.securityPhotoAt || fallback?.securityPhotoAt || '',
    securityPhotoVerified: profile?.isSecurityPhotoVerified ?? profile?.securityPhotoVerified ?? fallback?.securityPhotoVerified ?? false,
    securityPhotoVerification: profile?.securityPhotoVerification || fallback?.securityPhotoVerification,
    birthDate: profile?.birthDate || profile?.bioData?.dateOfBirth || fallback?.birthDate || '',
    gender: profile?.gender || profile?.bioData?.gender || fallback?.gender || '',
    otherGender: profile?.otherGender || fallback?.otherGender || '',
    status: profile?.status || fallback?.status || 'ACTIVE',
    ssn: profile?.ssn || fallback?.ssn || '',
    bioData: profile?.bioData || {
      dateOfBirth: profile?.birthDate || fallback?.birthDate || '',
      gender: profile?.gender || fallback?.gender || '',
      maritalStatus: profile?.maritalStatus || fallback?.maritalStatus || '',
      occupation: profile?.occupation || fallback?.occupation || '',
    },
    address: permanentAddress,
    permanentAddress,
    residentialAddress,
    workAddress,
    schoolAddress,
    billingAddress,
    preferredMailingAddress: profile?.preferredMailingAddress || fallback?.preferredMailingAddress || '',
    preferredContactMethod: profile?.preferredContactMethod || fallback?.preferredContactMethod || '',
    contactDetails: {
      ...contactDetails,
      mobilePhone: contactDetails.mobilePhone || profile?.phone || fallback?.phone || '',
      personalEmail: contactDetails.personalEmail || profile?.email || fallback?.email || '',
      email: contactDetails.email || profile?.email || fallback?.email || '',
    },
    identityVerified:
      identityRecordVerified ||
      profile?.identityVerified === true ||
      profile?.verified === true ||
      fallback?.identityVerified === true ||
      false,
    emailVerified: profile?.emailVerified || fallback?.emailVerified || false,
    phoneVerified: profile?.phoneVerified || fallback?.phoneVerified || false,
    isVerified: profile?.isVerified ?? profile?.verified ?? fallback?.isVerified ?? false,
    currentAppRole: profile?.currentAppRole || fallback?.currentAppRole,
    isRegisteredHelper: profile?.isRegisteredHelper ?? fallback?.isRegisteredHelper ?? false,
    userAgreementAcceptanceStatus: profile?.userAgreementAcceptanceStatus || fallback?.userAgreementAcceptanceStatus,
    acceptedAgreementVersion: profile?.acceptedAgreementVersion || fallback?.acceptedAgreementVersion,
    userIdentityVerification,
    userWorkAuthorization,
    helperProfile: profile?.helperProfile || fallback?.helperProfile,
    requesterProfile: profile?.requesterProfile || fallback?.requesterProfile,
    nextOfKin: profile?.nextOfKin || fallback?.nextOfKin,
    lastLoginAt: profile?.lastLoginAt || profile?.lastLogin || fallback?.lastLoginAt || fallback?.lastLogin || '',
    lastLogin: profile?.lastLogin || profile?.lastLoginAt || fallback?.lastLogin || fallback?.lastLoginAt || '',
  };
}

export function getAccessToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEYS.access);
}

export function getRefreshToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEYS.refresh);
}

export async function refreshTokens(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  const clientId = localStorage.getItem(STORAGE_KEYS.clientId);
  if (!refreshToken || !clientId) return false;

  try {
    const response = await fetch(`${API_BASE}/api/v1/auth/token/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'O-CLIENT-APP': CLIENT_APP,
        'O-API-KEY': API_KEY,
        'O-CLIENT-ID': clientId,
      },
      body: JSON.stringify({ refreshToken, deviceId: clientId }),
    });

    if (!response.ok) return false;
    const data = await response.json();
    if (!data?.accessToken) return false;
    saveSession(data, getUser());
    return true;
  } catch {
    return false;
  }
}

export function getUser(): any {
  try {
    const user = sessionStorage.getItem(STORAGE_KEYS.user);
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
}

export function isLoggedIn(): boolean {
  const loggedIn = !!getAccessToken();
  console.log('🔍 isLoggedIn check:', loggedIn);
  return loggedIn;
}

async function buildAuthHeaders(): Promise<Record<string, string>> {
  const clientId = await getOrCreateClientId();
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'O-CLIENT-APP': CLIENT_APP,
    'O-API-KEY': API_KEY,
    'O-CLIENT-ID': clientId || '',
  };
}

async function hydrateUserProfileAfterAuth(accessToken: string, fallback: Record<string, unknown> = {}) {
  console.log('📦 Hydrating user profile after auth...');
  
  try {
    const userProfile = await fetchUserProfile(accessToken);
    
    if (userProfile) {
      console.log('✅ User profile fetched from server');
      const userData = normalizeUserProfile(userProfile, fallback);
      saveUser(userData);
      return userData;
    } else {
      console.warn('⚠️ Server did not return user profile');
    }
  } catch (error) {
    console.warn('⚠️ Failed to fetch user profile:', error);
  }

  if (Object.keys(fallback).length) {
    console.log('📦 Using fallback profile');
    
    if (!fallback.id) {
      console.warn('⚠️ Fallback profile missing ID - profile completion may fail');
    }
    
    saveUser(fallback);
    return fallback;
  }
  
  console.error('❌ No profile data available - critical error');
  return null;
}

async function fetchUserProfile(token: string): Promise<any> {
  try {
    console.log('👤 Fetching user profile...');
    const clientId = localStorage.getItem(STORAGE_KEYS.clientId) || '';
    const response = await fetch(`${PROFILE_API_BASE}/api/v1/user-profiles/get?refresh=true`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'O-CLIENT-APP': CLIENT_APP,
        'O-API-KEY': API_KEY,
        'O-CLIENT-ID': clientId,
        'Content-Type': 'application/json',
      },
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('✅ User profile fetched successfully');
      return data;
    } else {
      console.warn('⚠️ Failed to fetch user profile:', response.status);
    }
  } catch (error) {
    console.error('❌ Failed to fetch user profile:', error);
  }
  return null;
}

async function performLoginRequest(credentials: { email: string; password: string }, clientId: string | null) {
  console.log('📨 Login attempt with:', {
    email: credentials.email,
    'O-CLIENT-APP': CLIENT_APP,
    'O-CLIENT-ID': clientId,
  });

  return fetch(`${API_BASE}/api/v1/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'O-CLIENT-APP': CLIENT_APP,
      'O-API-KEY': API_KEY,
      'O-CLIENT-ID': clientId || '',
    },
    body: JSON.stringify({ email: credentials.email, password: credentials.password }),
  });
}

export async function login(credentials: { email: string; password: string }): Promise<any> {
  const clientId = await getOrCreateClientId();
  let res = await performLoginRequest(credentials, clientId);

  let data;
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  console.log('📡 Login response status:', res.status);

  if (!res.ok) {
    const msg = data.message || data.error || 'Invalid email or password';
    const isInvalidClientId = /invalid or unknown client id/i.test(msg);

    if (isInvalidClientId && clientId) {
      console.warn('⚠️ Stale client ID detected, clearing and retrying login');
      localStorage.removeItem(STORAGE_KEYS.clientId);
      const freshClientId = await getOrCreateClientId();
      res = await performLoginRequest(credentials, freshClientId);

      try {
        data = await res.json();
      } catch {
        data = {};
      }

      console.log('📡 Login retry response status:', res.status);
    }

    if (!res.ok) {
      const retryMsg = data.message || data.error || 'Invalid email or password';
      console.error('❌ Login failed:', retryMsg);
      throw new Error(retryMsg);
    }
  }

  console.log('✅ Login successful, saving session...');
  
  saveSession(data, null);
  
  try {
    const userProfile = await fetchUserProfile(data.accessToken);
    if (userProfile) {
      const userData = normalizeUserProfile(userProfile, {
        email: credentials.email,
        username: credentials.email.split('@')[0],
      });
      saveUser(userData);
      console.log('✅ User profile saved:', { firstName: userData.firstName, lastName: userData.lastName, email: userData.email });
    } else {
      const basicUser = {
        email: credentials.email,
        firstName: credentials.email.split('@')[0],
        lastName: '',
        username: credentials.email.split('@')[0],
      };
      saveUser(basicUser);
      console.log('⚠️ Using fallback user info:', basicUser);
    }
  } catch (error) {
    console.error('❌ Failed to process user profile:', error);
    const basicUser = {
      email: credentials.email,
      firstName: credentials.email.split('@')[0],
      lastName: '',
      username: credentials.email.split('@')[0],
    };
    saveUser(basicUser);
    console.log('⚠️ Using emergency fallback user info:', basicUser);
  }

  const savedUser = getUser();
  console.log('👤 Final user in storage:', savedUser);

  return data;
}

export async function signup(data: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  username: string;
  phone?: string;
  middleName?: string;
}): Promise<any> {
  const clientId = await getOrCreateClientId();
  
  console.log('📝 Signup attempt:', { email: data.email, username: data.username });
  
  const res = await fetch(`${API_BASE}/api/v1/auth/signup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'O-CLIENT-APP': CLIENT_APP,
      'O-API-KEY': API_KEY,
      'O-CLIENT-ID': clientId || '',
    },
    body: JSON.stringify({
      username: data.username,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      password: data.password,
      isSocial: false,
      ...(data.middleName ? { middleName: data.middleName } : {}),
      ...(data.phone ? { phone: data.phone } : {}),
    }),
  });

  let responseData;
  try {
    responseData = await res.json();
  } catch {
    responseData = {};
  }

  console.log('📡 Signup response status:', res.status);

  if (!res.ok) {
    const msg = responseData.message || `Registration failed (${res.status})`;
    console.error('❌ Signup failed:', msg);
    throw new Error(msg);
  }
  
  console.log('✅ Signup successful for:', data.email);
  return responseData;
}

async function createInitialUserProfile(signupData: any, registeredUser: any, token: string): Promise<any | null> {
  const userId = registeredUser?.id || registeredUser?.userId;
  if (!userId) return null;

  const payload = buildRegistrationProfilePayload({
    id: userId,
    firstName: signupData.firstName,
    middleName: signupData.middleName,
    lastName: signupData.lastName,
    username: signupData.username,
    email: signupData.email,
    phone: signupData.phone,
    status: registeredUser?.status || 'ACTIVE',
    bioData: signupData.bioData,
    address: signupData.address,
    permanentAddress: signupData.permanentAddress,
    profilePhoto: signupData.profilePhoto,
    picture: signupData.picture,
  });

  try {
    const response = await fetch(`${PROFILE_API_BASE}/api/v1/user-profiles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'O-CLIENT-APP': CLIENT_APP,
        'O-API-KEY': API_KEY,
        'O-CLIENT-ID': localStorage.getItem(STORAGE_KEYS.clientId) || '',
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      return await response.json();
    }

    if (response.status !== 409) {
      const text = await response.text().catch(() => '');
      console.warn('Failed to create initial user profile:', response.status, text);
    }
  } catch (error) {
    console.warn('Initial user profile creation failed:', error);
  }

  return null;
}

export async function signupAndLogin(data: any): Promise<any> {
  console.log('🔄 Starting signup and login flow...');
  const registeredUser = await signup(data);
  console.log('✅ Signup complete, now logging in...');
  await login({ email: data.email, password: data.password });
  const token = getAccessToken();
  const storedUser = getUser();
  if (token && registeredUser?.id && !storedUser?.id) {
    const createdProfile = await createInitialUserProfile(data, registeredUser, token);
    const refreshedProfile = await fetchUserProfile(token);
    const userData = normalizeUserProfile(refreshedProfile || createdProfile, {
      id: registeredUser.id,
      firstName: data.firstName,
      middleName: data.middleName || '',
      lastName: data.lastName,
      email: data.email,
      username: data.username,
      phone: data.phone || '',
    });
    saveUser(userData);
  }
  console.log('✅ Signup and login complete');
  return registeredUser;
}

export async function googleLogin(credential: string): Promise<any> {
  console.log('📨 Google login attempt...');

  const res = await fetch(`${API_BASE}/api/v1/auth/google/admin`, {
    method: 'POST',
    headers: await buildAuthHeaders(),
    body: JSON.stringify({ idToken: credential }),
  });

  let data: any;
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok) {
    throw new Error(data.message || data.error || data.humanMessage || 'Google sign-in failed');
  }

  if (data?.requiresPassword || data?.requiresLinkApproval) {
    console.log('ℹ️ Google auth requires further action:', {
      requiresPassword: data?.requiresPassword,
      requiresLinkApproval: data?.requiresLinkApproval,
    });
    return data;
  }

  if (!data?.accessToken) {
    throw new Error('Google sign-in did not return an access token.');
  }

  let fallback: Record<string, unknown> = {};
  try {
    const payload = JSON.parse(atob(credential.split('.')[1]));
    fallback = {
      email: payload.email || '',
      firstName: payload.given_name || '',
      lastName: payload.family_name || '',
      username: payload.email ? String(payload.email).split('@')[0] : '',
      picture: payload.picture || '',
    };
  } catch {
    console.warn('⚠️ Could not extract Google token claims');
  }

  saveSession(data, null);
  await hydrateUserProfileAfterAuth(data.accessToken, fallback);
  return data;
}

// =========== MICROSOFT LOGIN (identity-service) ===========
export async function microsoftLogin(idToken: string): Promise<any> {
  console.log('📨 Microsoft login attempt...');

  const res = await fetch(`${API_BASE}/api/v1/auth/microsoft`, {
    method: 'POST',
    headers: await buildAuthHeaders(),
    body: JSON.stringify({ idToken }),
  });

  let data: any;
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok) {
    throw new Error(data.message || data.error || data.humanMessage || 'Microsoft sign-in failed');
  }

  if (data?.requiresPassword || data?.requiresLinkApproval) {
    console.log('ℹ️ Microsoft auth requires further action:', {
      requiresPassword: data?.requiresPassword,
      requiresLinkApproval: data?.requiresLinkApproval,
    });
    return data;
  }

  if (!data?.accessToken) {
    throw new Error('Microsoft sign-in did not return an access token.');
  }

  saveSession(data, null);
  await hydrateUserProfileAfterAuth(data.accessToken, {});
  return data;
}

// =========== LINKEDIN WEB SIGN-IN (identity-service) ===========
export async function initiateLinkedInWebSignIn(returnTo: string): Promise<{ redirectUrl: string }> {
  const res = await fetch(
    `${API_BASE}/api/v1/auth/linkedin/web?returnTo=${encodeURIComponent(returnTo)}`,
    {
      headers: await buildAuthHeaders(),
    },
  );

  if (!res.ok) {
    throw new Error('Failed to initiate LinkedIn sign-in');
  }

  const data = await res.json();
  return { redirectUrl: data.redirect_url || data.redirectUrl || '' };
}

export async function linkedinLogin(tokenData: {
  accessToken: string;
  refreshToken?: string;
  accessTokenExpiresAt?: number;
  refreshTokenExpiresAt?: number;
}): Promise<any> {
  const bearerToken = tokenData.accessToken.startsWith('Bearer ')
    ? tokenData.accessToken
    : `Bearer ${tokenData.accessToken}`;

  saveSession({ accessToken: bearerToken, refreshToken: tokenData.refreshToken }, null);

  if (tokenData.accessTokenExpiresAt) {
    setSessionItem(STORAGE_KEYS.expiry, String(tokenData.accessTokenExpiresAt));
  }

  await hydrateUserProfileAfterAuth(bearerToken, {});
  return { success: true, data: { accessToken: bearerToken } };
}

// =========== APPLE LOGIN (identity-service) ===========
export async function appleLogin(appleResponse: any, password?: string): Promise<any> {
  const authorization = appleResponse.authorization || {};
  const identityToken = authorization.id_token || appleResponse.identityToken || appleResponse.id_token;

  if (!identityToken) {
    console.error('❌ Apple sign-in: no id_token in authorization response');
    throw new Error('Apple sign-in failed: no id_token received.');
  }

  const appleUser = typeof appleResponse.user === 'object' && appleResponse.user ? appleResponse.user : {};
  const jwtPayloadFirst = decodeJwtPayload(identityToken) || {};

  const firstName =
    appleUser.name?.firstName ||
    appleUser.name?.givenName ||
    jwtPayloadFirst.given_name ||
    appleResponse.firstName ||
    '';
  const lastName =
    appleUser.name?.lastName ||
    appleUser.name?.familyName ||
    jwtPayloadFirst.family_name ||
    appleResponse.lastName ||
    '';
  const email =
    appleUser.email ||
    appleResponse.email ||
    jwtPayloadFirst.email ||
    '';
  const phone =
    appleUser.phone ||
    appleResponse.phone ||
    jwtPayloadFirst.phone_number ||
    '';
  const appleUserId =
    (typeof appleResponse.user === 'string' ? appleResponse.user : '') ||
    jwtPayloadFirst.sub ||
    appleResponse.userId ||
    '';
  const fullName = firstName || lastName
    ? {
        givenName: firstName || '',
        familyName: lastName || '',
      }
    : undefined;

  console.log('📨 Sending Apple auth request to server...');

  const res = await fetch(`${API_BASE}/api/v1/auth/apple`, {
    method: 'POST',
    headers: await buildAuthHeaders(),
    body: JSON.stringify({
      identityToken,
      authorizationCode: authorization.code || appleResponse.authorizationCode || null,
      user: appleUserId,
      fullName,
      email,
      firstName,
      lastName,
      phone,
      ...(password ? { password } : {}),
    }),
  });

  let data: any;
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok) {
    const errorMsg = data.message || data.error || data.humanMessage || 'Apple sign-in failed';
    console.error('❌ Apple sign-in server error:', {
      status: res.status,
      message: errorMsg,
      responseBody: data,
    });
    throw new Error(errorMsg);
  }

  if (data?.requiresPassword || data?.requiresLinkApproval) {
    console.log('ℹ️ Apple auth requires further action:', {
      requiresPassword: data?.requiresPassword,
      requiresLinkApproval: data?.requiresLinkApproval,
      tempToken: data?.tempToken ? '***' : undefined,
    });
    logSecurityEvent('APPLE_AUTH_INTERMEDIATE', {
      requiresPassword: data?.requiresPassword,
      requiresLinkApproval: data?.requiresLinkApproval,
    });
    return data;
  }

  if (!data?.accessToken) {
    console.error('❌ Apple sign-in: server did not return accessToken');
    throw new Error('Apple sign-in did not return an access token.');
  }

  // ✅ FIX: Remove invalid assignment to undefined 'backendToken'
  // The original line "data.accessToken = backendToken;" was removed because backendToken doesn't exist.
  // The accessToken from server is already correct, so we do nothing extra.
  // (If you intended to use a temporary token, you should store it in a separate variable.)

  // ✅ FIX: Variable redeclaration – use separate names
  let userId = data?.userId || data?.user?.id || data?.id;
  
  let jwtPayloadSecond: any = {};
  let fallback: Record<string, unknown> = {};
  
  try {
    jwtPayloadSecond = JSON.parse(atob(identityToken.split('.')[1]));
    
    if (!userId) {
      userId = jwtPayloadSecond.sub;
      console.log('ℹ️ Using JWT sub as userId:', userId?.slice(0, 8) + '...');
    }
    
    const emailFromToken = appleUser.email || jwtPayloadSecond.email || '';
    const isPrivateRelay = emailFromToken?.includes('@privaterelay.appleid.com');
    
    let username = '';
    if (emailFromToken && !isPrivateRelay) {
      username = emailFromToken.split('@')[0];
    } else {
      username = `user_${jwtPayloadSecond.sub?.slice(0, 8) || Math.random().toString(36).slice(2, 8)}`;
    }

    fallback = {
      id: userId,
      email: emailFromToken,
      firstName: appleUser.name?.firstName || jwtPayloadSecond.given_name || '',
      lastName: appleUser.name?.lastName || jwtPayloadSecond.family_name || '',
      username: username,
      picture: '',
      isPrivateEmail: isPrivateRelay,
      profileIncomplete: true,
      authMethod: 'APPLE',
    };
    
    console.log('✅ Apple profile extracted:', { 
      userId: userId?.slice(0, 8) + '...', 
      username, 
      isPrivateEmail: isPrivateRelay 
    });
  } catch (err) {
    console.warn('⚠️ Could not extract Apple token claims:', err);
    logSecurityEvent('APPLE_JWT_DECODE_FAILED', { error: String(err) });
    if (jwtPayloadSecond?.sub && !userId) {
      userId = jwtPayloadSecond.sub;
      fallback.id = userId;
    }
  }

  console.log('✅ Apple sign-in successful, saving session...');
  saveSession(data, null);
  
  const profile = await hydrateUserProfileAfterAuth(data.accessToken, fallback);
  
  if (!profile?.id && userId) {
    console.log('⚠️ Profile missing ID, forcing refresh...');
    const refreshed = await fetchUserProfile(data.accessToken);
    if (refreshed?.id) {
      const normalized = normalizeUserProfile(refreshed, fallback);
      saveUser(normalized);
      console.log('✅ Profile refreshed successfully');
    } else if (profile) {
      const withId = { ...profile, id: userId };
      saveUser(withId);
      console.log('✅ Ensured ID in stored profile');
    }
  }
  
  logSecurityEvent('APPLE_LOGIN_SUCCESS', { 
    username: fallback.username,
    hasUserId: !!userId 
  });
  
  return data;
}

// =========== LINK APPLE TO EXISTING ACCOUNT ===========
export async function linkAppleToBackend(
  email: string,
  appleUserId: string,
  password?: string,
  identityToken?: string,
  verificationCode?: string,
  linkingProcessId?: string
): Promise<any> {
  const clientId = await getOrCreateClientId();
  const headers = {
    'Content-Type': 'application/json',
    'O-CLIENT-APP': CLIENT_APP,
    'O-API-KEY': API_KEY,
    'O-CLIENT-ID': clientId || '',
  };

  const body: any = {
    email,
    appleUserId,
    identityToken,
  };
  if (password) body.password = password;
  if (verificationCode) body.verificationCode = verificationCode;
  if (linkingProcessId) body.linkingProcessId = linkingProcessId;

  const response = await fetch(`${API_BASE}/api/v1/auth/apple/link`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to link Apple account');
  }

  if (data.accessToken) {
    saveSession(data, null);
    await hydrateUserProfileAfterAuth(data.accessToken, { email });
  }

  return data;
}

function logSecurityEvent(event: string, details: any): void {
  const timestamp = new Date().toISOString();
  const sanitized = JSON.stringify(details).slice(0, 500);
  console.log(`🔐 [${event}] ${timestamp}:`, sanitized);
}

export async function startAppleWebSignIn(): Promise<void> {
  const headers = await buildAuthHeaders();
  delete headers['Content-Type'];

  const res = await fetch(`${API_BASE}/api/v1/auth/apple/web`, {
    method: 'GET',
    headers,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.redirect_url) {
    throw new Error(data.message || data.error || 'Unable to start Apple sign-in.');
  }

  window.location.href = data.redirect_url;
}

export async function logout(redirectTo: string = '/signin'): Promise<void> {
  const refreshToken = getRefreshToken();
  const clientId = localStorage.getItem(STORAGE_KEYS.clientId);
  
  console.log('🔒 Logging out...');
  
  try {
    if (refreshToken) {
      await fetch(`${API_BASE}/api/v1/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'O-CLIENT-APP': CLIENT_APP,
          'O-API-KEY': API_KEY,
          'O-CLIENT-ID': clientId || '',
        },
        body: JSON.stringify({ refreshToken }),
      });
      console.log('✅ Server logout successful');
    }
  } catch (error) { 
    console.warn('⚠️ Server logout failed, clearing locally anyway:', error);
  } finally {
    clearSession();
    console.log('🚪 Redirecting to:', redirectTo);
    window.location.href = redirectTo;
  }
}

export function requireAuth(redirectTo: string = '/signin'): void {
  if (!isLoggedIn()) {
    console.log('🔒 Auth required, redirecting to:', redirectTo);
    window.location.href = redirectTo;
  }
}

function newCorrelationId(): string {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export async function startPasswordRecovery(identifier: string): Promise<any> {
  const clientId = await getOrCreateClientId();
  
  console.log('📨 Starting password recovery for:', identifier);
  
  const res = await fetch(`${API_BASE}/api/v1/auth/recovery/code/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'O-CLIENT-APP': CLIENT_APP,
      'O-API-KEY': API_KEY,
      'O-CLIENT-ID': clientId || '',
      'O-CORRELATION-ID': newCorrelationId(),
    },
    body: JSON.stringify({ identifier }),
  });

  let data;
  try { data = await res.json(); } catch { data = {}; }

  console.log('📡 Password recovery response status:', res.status);

  if (!res.ok) {
    throw new Error(data.message || data.error || 'Could not send recovery code.');
  }
  
  console.log('✅ Recovery code sent');
  return data;
}

export async function verifyPasswordRecoveryCode(otpToken: string, code: string): Promise<any> {
  const clientId = await getOrCreateClientId();
  
  console.log('📨 Verifying recovery code...');
  
  const res = await fetch(`${API_BASE}/api/v1/auth/recovery/code/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'O-CLIENT-APP': CLIENT_APP,
      'O-API-KEY': API_KEY,
      'O-CLIENT-ID': clientId || '',
      'O-CORRELATION-ID': newCorrelationId(),
    },
    body: JSON.stringify({ otpToken, code }),
  });

  let data;
  try { data = await res.json(); } catch { data = {}; }

  console.log('📡 Verify code response status:', res.status);

  if (!res.ok) {
    throw new Error(data.message || data.error || 'Invalid or expired recovery code.');
  }
  
  console.log('✅ Code verified successfully');
  return data;
}

export async function resetPassword(resetToken: string, newPassword: string, confirmPassword: string): Promise<void> {
  const clientId = await getOrCreateClientId();
  
  console.log('📨 Resetting password...');
  
  const res = await fetch(`${API_BASE}/api/v1/auth/recovery/reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-App': CLIENT_APP,
      'O-API-KEY': API_KEY,
      'O-CLIENT-ID': clientId || '',
    },
    body: JSON.stringify({ resetToken, newPassword, confirmPassword }),
  });

  console.log('📡 Reset password response status:', res.status);

  if (!res.ok) {
    let data;
    try { data = await res.json(); } catch { data = {}; }
    throw new Error(data.message || data.error || 'Could not reset password.');
  }
  
  console.log('✅ Password reset successfully');
}
